import { unwrapList, unwrapObject, getAccessToken, toBase64 } from './apiUtils';
import { getWarehouseId, getTerminalToken, getTicketSeries, setTicketSeries } from './TerminalService';
import { CartItem } from '../types/book.types';
import { Transaction } from '../types/transaction.types';
import {
    call,
    mapLine,
    toNumber,
    TAX_RATE,
    REFUND_ROUNDING_MARGIN,
    REFUND_TOLERANCE
} from './SalesService';

/**
 * Un ticket es el registro de la venta (o de la devolucion) en el TPV, con o
 * sin cliente. La factura es opcional y cuelga de el: se crea con
 * invoices_create(ticket_id) y se enlaza con tickets_link_invoice.
 *
 * Los nombres de ruta son los DESPLEGADOS, que no son uniformes: list y
 * refundable estan en singular, el resto en plural.
 */
const ROUTES = {
    list: '/ticket_list',
    get: '/tickets_get',
    create: '/tickets_create',
    refundable: '/ticket_refundable',
    linkInvoice: '/tickets_link_invoice'
};

/**
 * Cliente al que se factura la venta sin identificar. Por decision del negocio
 * es el 25, el mismo que invoices_create usa por defecto. OJO: en los datos
 * actuales ese cliente es "FNAC Espana" (A28999888), no un "varios" generico,
 * asi que estas facturas salen a su nombre. Cuando exista un cliente "varios"
 * propio, basta con cambiar este id (o dejarlo vacio para que solo facturen
 * las ventas con cliente; el ticket se crea siempre, con client_id NULL).
 */
export const VARIOS_CLIENT_ID = '25';

const PAYMENT_METHODS = ['cash', 'card', 'marcial_card', 'gift_card', 'invoice'];

const mapTicket = (raw: any): Transaction => {
    const method = String(raw.payment_method || '').toLowerCase();
    const invoiceId = raw.invoice_id === undefined || raw.invoice_id === null
        ? undefined
        : String(raw.invoice_id);

    return {
        id: String(raw.id),
        ticketNumber: `${raw.series}-${raw.ticket_number}`,
        timestamp: new Date(raw.created_at || Date.now()),
        // `id` de la linea es el de ticket_lines; el libro viaja aparte para poder devolver su stock.
        items: Array.isArray(raw.lines)
            ? raw.lines.map((l: any) => ({
                ...mapLine(l),
                productId: l.product_id === undefined || l.product_id === null ? undefined : String(l.product_id)
            }))
            : [],
        total: Math.abs(toNumber(raw.total)),
        paymentMethod: (PAYMENT_METHODS.indexOf(method) === -1 ? 'invoice' : method) as Transaction['paymentMethod'],
        type: raw.ticket_type === 'return' ? 'return' : 'sale',
        clientId: raw.client_id === undefined || raw.client_id === null ? undefined : String(raw.client_id),
        clientName: raw.client_name || undefined,
        isTaxFree: !!raw.is_tax_free,
        invoiceId,
        invoiceNumber: raw.invoice_number || undefined,
        refundsTicketId: raw.original_ticket_id ? String(raw.original_ticket_id) : undefined,
        // tickets_get trae la serie y el numero del original; ticket_list solo su id.
        refundsTicketNumber: raw.original_series ? `${raw.original_series}-${raw.original_ticket_number}` : undefined
    };
};

/**
 * "Hoy" en hora de Madrid, que es la zona con la que ticket_list convierte
 * created_at. Con la fecha local del navegador, un equipo en otra zona pedia
 * el dia equivocado y no veia los tickets recien creados. en-CA da YYYY-MM-DD.
 */
const localDay = (): string =>
    new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Madrid' });

/**
 * Tickets de esta caja. Sin lineas: se piden con findTicket al abrir uno.
 *
 * Con `cashSessionId` son los de la sesion de caja abierta, que es lo que el
 * arqueo cuenta; el dia se manda igualmente y el servidor lo ignora si recibe
 * la sesion (una version antigua de ticket_list ignora la sesion y filtra por
 * dia, que es lo que se tenia antes).
 */
export const getTicketHistory = async (cashSessionId?: string): Promise<Transaction[]> => {
    const accessToken = getAccessToken();
    if (!accessToken) throw new Error('No autorizado');

    const res = await call(ROUTES.list, {
        access_token: accessToken,
        day: localDay(),
        cash_session_id: cashSessionId,
        warehouse_id: getWarehouseId(),
        limit: 500
    });
    return unwrapList(res).map(mapTicket);
};

export interface TicketRefundStatus {
    total: number;
    refunded: number;
    available: number;
}

export const getTicketRefundStatus = async (ticketId: string): Promise<TicketRefundStatus> => {
    const accessToken = getAccessToken();
    if (!accessToken) throw new Error('No autorizado');

    const res = await call(ROUTES.refundable, { access_token: accessToken, id: ticketId });
    const row = unwrapObject(res) || {};
    return {
        total: Math.abs(toNumber(row.total)),
        refunded: Math.abs(toNumber(row.refunded_amount)),
        available: Math.max(0, toNumber(row.refundable_amount))
    };
};

/**
 * Cuanto se va a devolver de verdad, ya autorizado por el backend. Misma regla
 * de redondeo que resolveRefundAmount de SalesService: si lo pedido se pasa del
 * disponible por menos del margen, se ajusta al saldo; si se pasa de mas, se
 * manda tal cual y que lo rechace el backend con su mensaje.
 */
export const resolveTicketRefundAmount = async (ticketId: string, requested: number): Promise<number> => {
    const { available } = await getTicketRefundStatus(ticketId);

    const excess = requested - available;
    const isRounding = excess > 0 && excess <= REFUND_ROUNDING_MARGIN && available >= REFUND_TOLERANCE;
    const amount = isRounding ? available : requested;

    const accessToken = getAccessToken();
    if (!accessToken) throw new Error('No autorizado');
    await call(ROUTES.refundable, { access_token: accessToken, id: ticketId, amount: amount.toFixed(2) });

    return amount;
};

/** Carga un ticket con sus lineas. undefined = no existe. */
const getTicketById = async (id: string): Promise<Transaction | undefined> => {
    const accessToken = getAccessToken();
    if (!accessToken) throw new Error('No autorizado');

    let res: any;
    try {
        res = await call(ROUTES.get, { access_token: accessToken, id });
    } catch (err) {
        if (String(err?.message).toLowerCase().indexOf('no encontrado') !== -1) return undefined;
        throw err;
    }

    const raw = unwrapObject(res);
    if (!raw || !raw.id) return undefined;

    return { ...mapTicket(raw), refundableAmount: Math.max(0, toNumber(raw.refundable_amount)) };
};

/** Busca en ticket_list por numero de ticket, numero de factura o id de factura. */
const searchTickets = async (term: string): Promise<any[]> => {
    // El backend solo acepta letras, digitos, guion y guion bajo en `search`.
    if (!/^[A-Za-z0-9_-]{1,30}$/.test(term)) return [];

    const accessToken = getAccessToken();
    if (!accessToken) throw new Error('No autorizado');

    const res = await call(ROUTES.list, { access_token: accessToken, search: term, limit: 50 });
    return unwrapList(res);
};

const ticketNumberOf = (row: any): string => `${row.series}-${row.ticket_number}`.toUpperCase();

/**
 * Id interno del ticket a partir de lo que teclea el cajero: el numero del
 * ticket (T1-6), el numero de la factura (TPV-...) o su id.
 *
 * Un numero suelto es el ticket de ESTA caja (serie T<almacen>), no un id
 * interno: con dos cajas el id 6 podria ser el T2-3. Solo si no existe se prueba
 * como id de factura. Gana la coincidencia exacta; sin ella solo se acepta un
 * unico resultado, y si hay varios se pide el numero completo en vez de cargar
 * uno cualquiera.
 */
const resolveTicketId = async (ref: string): Promise<string | undefined> => {
    const target = ref.toUpperCase();
    const isDigits = /^\d+$/.test(ref);

    if (isDigits) {
        const warehouseId = getWarehouseId();
        const series = getTicketSeries() || (warehouseId ? `T${warehouseId}` : 'T');
        const own = `${series}-${ref}`.toUpperCase();
        const ownRows = await searchTickets(own);
        const hit = ownRows.find(r => ticketNumberOf(r) === own);
        if (hit) return String(hit.id);
    }

    const rows = await searchTickets(ref);
    const exact =
        rows.find(r => ticketNumberOf(r) === target) ||
        rows.find(r => String(r.invoice_number || '').toUpperCase() === target) ||
        (isDigits ? rows.find(r => String(r.invoice_id) === ref) : undefined);
    if (exact) return String(exact.id);

    // Un numero suelto solo vale si es exacto: "999" no debe cargar el ticket
    // cuyo numero de factura lo contiene a mitad.
    if (isDigits) return undefined;

    if (rows.length === 1) return String(rows[0].id);
    if (rows.length > 1) {
        throw new Error('Varios tickets coinciden: escribe el número completo del ticket o de la factura.');
    }
    return undefined;
};

/**
 * Busca un ticket para devolverlo, por numero de ticket o de factura. Lo que no
 * da nada que devolver se corta aqui, no al confirmar.
 */
export const findTicket = async (ref: string): Promise<Transaction | undefined> => {
    const cleanRef = ref.trim();
    if (!cleanRef) return undefined;

    const id = await resolveTicketId(cleanRef);
    if (!id) return undefined;

    const ticket = await getTicketById(id);
    if (!ticket) return undefined;

    if (ticket.type === 'return') {
        throw new Error(`El ticket ${ticket.ticketNumber} es una devolución: no se puede devolver.`);
    }
    // El plazo de devolucion NO corta: el ticket se carga igual y quien avisa es
    // la pantalla (insignia en el buscador y en el ticket cargado). La decision
    // de devolver fuera de plazo es del cajero, no del TPV.
    if ((ticket.refundableAmount || 0) < REFUND_ROUNDING_MARGIN) {
        throw new Error(`El ticket ${ticket.ticketNumber} ya se ha devuelto por completo.`);
    }
    return ticket;
};

/** Carga un ticket con sus lineas sin las reglas de devolucion (para el modal de Movimientos). */
export const getTicket = getTicketById;

/**
 * Ticket por su numero (T1-6), el de su factura o su id, SIN las reglas de
 * devolucion de findTicket: aqui el ticket se lee para consultarlo, no para
 * devolverlo, y uno ya devuelto del todo sigue siendo una lectura valida.
 */
export const findTicketByRef = async (ref: string): Promise<Transaction | undefined> => {
    const cleanRef = ref.trim();
    if (!cleanRef) return undefined;

    const id = await resolveTicketId(cleanRef);
    return id ? getTicketById(id) : undefined;
};

const bookIdOf = (item: CartItem, isSale: boolean): number | null => {
    const raw = isSale ? item.id : item.productId;
    return raw !== undefined && /^\d+$/.test(String(raw)) ? Number(raw) : null;
};

const toLines = (items: CartItem[], promoPct: number, withProduct: boolean) =>
    items.map(item => {
        const factor = (1 - (item.discount || 0) / 100) * (1 - promoPct / 100);
        return {
            // Una venta lleva el libro en item.id. En una devolucion item.id es el de la
            // linea del ticket original: el libro sale de productId, para que el
            // servidor sepa a que stock devolver las unidades.
            product_id: bookIdOf(item, withProduct),
            description: item.title,
            quantity: item.quantity,
            unit_price: item.price.toFixed(2),
            discount_pct: (100 * (1 - factor)).toFixed(2),
            tax_rate: TAX_RATE,
            line_total: (item.price * item.quantity * factor).toFixed(2)
        };
    });

export interface CreatedTicket {
    id: string;
    number: string;
}

interface TicketPayload {
    items: CartItem[];
    total: number;
    paymentMethod: string;
    clientId?: string;
    authorizedPersonId?: string;
    isTaxFree?: boolean;
    promoPct?: number;
}

const createTicket = async (
    type: 'sale' | 'return',
    p: TicketPayload,
    originalTicketId?: string
): Promise<CreatedTicket> => {
    const accessToken = getAccessToken();
    if (!accessToken) throw new Error('No autorizado');

    const promoPct = p.promoPct || 0;
    // Los precios del catalogo ya llevan IVA incluido: la base se extrae del total.
    const subtotal = p.total / (1 + TAX_RATE / 100);
    const warehouseId = getWarehouseId();

    const res = await call(ROUTES.create, {
        access_token: accessToken,
        warehouse_id: warehouseId,
        // Con terminal_token la serie la decide el servidor (linea de facturacion de
        // la tienda, o T<almacen>); solo se manda la de siempre si no hay terminal.
        terminal_token: getTerminalToken() || undefined,
        series: getTerminalToken() ? undefined : (warehouseId ? `T${warehouseId}` : 'T'),
        ticket_type: type,
        original_ticket_id: originalTicketId,
        client_id: p.clientId,
        // El modal de cliente rellena 'generic' ("Compra Directa") cuando el
        // cliente no tiene autorizados: no es un id de authorized_persons.
        authorized_person_id: /^\d+$/.test(String(p.authorizedPersonId || '')) ? p.authorizedPersonId : undefined,
        payment_method: p.paymentMethod,
        // El importe viaja en positivo: el backend lo niega en las devoluciones.
        subtotal: subtotal.toFixed(2),
        tax_amount: (p.total - subtotal).toFixed(2),
        total: p.total.toFixed(2),
        promo_pct: promoPct,
        // Solo se manda cuando es true: el gateway convierte 'true'/'false' en
        // booleanos de Python y el backend los recibe como True/False. Omitido,
        // tickets_create usa su defecto 'false'.
        is_tax_free: p.isTaxFree ? 'true' : undefined,
        // En base64 y no en JSON claro: el gateway parsea el JSON de la query y
        // el .avap recibiria una lista de Python (comillas simples), no el texto.
        lines: toBase64(JSON.stringify(toLines(p.items, promoPct, type === 'sale')))
    });

    const row = unwrapObject(res);
    if (!row || !row.id) throw new Error('El backend no devolvio el id del ticket.');
    if (row.series) setTicketSeries(String(row.series));
    return { id: String(row.id), number: `${row.series}-${row.ticket_number}` };
};

export const registerTicketSale = (p: TicketPayload): Promise<CreatedTicket> =>
    createTicket('sale', p);

export const registerTicketReturn = (originalTicketId: string, p: TicketPayload): Promise<CreatedTicket> =>
    createTicket('return', p, originalTicketId);

/** Enlaza el ticket con la factura creada con su ticket_id. */
export const linkTicketInvoice = async (ticketId: string, invoiceId: string): Promise<void> => {
    const accessToken = getAccessToken();
    if (!accessToken) throw new Error('No autorizado');

    await call(ROUTES.linkInvoice, { access_token: accessToken, ticket_id: ticketId, invoice_id: invoiceId });
};

